import { injectable } from "tsyringe";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { strings } from "utils/localizedStrings";
import { PackageLookupInjection } from "features/packageLookup/di/PackageLookupInjection";
import { PackageLookupRequestEntity } from "features/packageLookup/domain/entities/PackageLookupRequestEntity";
import { PackageLookupResponseEntity } from "features/packageLookup/domain/entities/PackageLookupResponseEntity";
import { BehaviorSubject } from "rxjs";

// ─── Input ────────────────────────────────────────────────────────────────────

class PackageLookupInput implements Disposable {
  public fetchPackageLookup = new BehaviorSubject<PackageLookupRequestEntity>(undefined);

  [Symbol.dispose](): void {
    this.fetchPackageLookup.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class PackageLookupOutput implements Disposable {
  public savedLookupData = new BehaviorSubject<PackageLookupResponseEntity>(undefined);
  public onError = new BehaviorSubject<string>("");
  public loading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedLookupData.complete();
    this.onError.complete();
    this.loading.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class PackageLookupViewModel extends BaseViewModel<
  PackageLookupInput,
  PackageLookupOutput
> {
  constructor() {
    super(new PackageLookupInput(), new PackageLookupOutput());
  }

  private useCase = PackageLookupInjection.getUseCase();

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchPackageLookup, async (params) => {
      try {
        this.output.loading.next(true);
        const res = await this.useCase.viewPlan(params);
        if (res?.data) {
          this.output.savedLookupData.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.loading.next(false);
      }
    });
  }
}
